import {
  FEATURE_FLAGS_KEY,
  posthogFeatureFlags
} from '@/shared/constants/constants';
import {
  hobbiiLocalStorage,
  hobbiiSessionStorage
} from '@/shared/utils/web-storage';

import type { FeatureFlags, FeatureFlagValue } from './types';

const getNormalizedFeatureFlagValue = (
  value: FeatureFlagValue
): string | boolean => {
  return typeof value === 'string' ? value.toLowerCase() : false;
};

/**
 * Resolves the value of a feature flag
 * If the value is 'control', it returns false
 * Otherwise, it returns the value as is
 */
const resolveFeatureFlagValue = (
  value: FeatureFlagValue | undefined
): FeatureFlagValue => {
  if (
    value === undefined ||
    getNormalizedFeatureFlagValue(value) === 'control'
  ) {
    return false;
  }

  return value;
};

// For a true multi-variant experiment (3+ named variants), where "not
// control" isn't specific enough. Unused while every active flag is a plain
// control/test toggle - keep isFeatureFlagEnabled as the default for those.
export const isFeatureFlagVariant = (
  value: FeatureFlagValue,
  ...variants: string[]
): boolean => {
  const normalizedValue = getNormalizedFeatureFlagValue(value);
  if (!normalizedValue) {
    return false;
  }
  return variants.some((variant) => variant.toLowerCase() === normalizedValue);
};

export const isFeatureFlagEnabled = (value: FeatureFlagValue): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return getNormalizedFeatureFlagValue(value) !== 'control';
  }
  return false;
};

export const getStoredFeatureFlags = (): FeatureFlags => {
  const storedFlags = hobbiiSessionStorage.getItem(FEATURE_FLAGS_KEY);

  if (!storedFlags) {
    return {};
  }

  try {
    return JSON.parse(storedFlags);
  } catch {
    console.warn('Failed to parse stored feature flags');
    return {};
  }
};

export const setFeatureFlag = (
  flagName: string,
  value: FeatureFlagValue
): void => {
  const currentFlags = getStoredFeatureFlags();
  const updatedFlags = { ...currentFlags, [flagName]: value };
  hobbiiSessionStorage.setItem(FEATURE_FLAGS_KEY, JSON.stringify(updatedFlags));
};

export const getStoredFeatureFlag = (
  flagName: string
): FeatureFlagValue | undefined => {
  const storedFlags = getStoredFeatureFlags();
  return storedFlags[flagName];
};

export const initFeatureFlag = (
  flagName: string
): FeatureFlagValue | undefined => {
  const existingFlag = getStoredFeatureFlag(flagName);

  if (existingFlag !== undefined) {
    return existingFlag;
  }

  const featureFlag = window.posthog?.getFeatureFlag?.(flagName);

  if (featureFlag === undefined) {
    return undefined;
  }

  const flagValue = resolveFeatureFlagValue(featureFlag);
  setFeatureFlag(flagName, flagValue);
  return flagValue;
};

const clearFeatureFlagsOnce = (): void => {
  if (hobbiiLocalStorage.getItem('posthog_feature_flags_cleared')) {
    return;
  }

  hobbiiSessionStorage.removeItem(FEATURE_FLAGS_KEY);
  hobbiiLocalStorage.setItem('posthog_feature_flags_cleared', 'true');
};

// Evaluating a flag logs an exposure, so page-scoped experiment flags are
// only evaluated on demand via cacheFeatureFlag on the page that uses them.
const LAZY_FEATURE_FLAGS = new Set([
  posthogFeatureFlags.PATTERNS_LANDING_TEST,
  posthogFeatureFlags.RECO_PDP_ALSO_BOUGHT,
  posthogFeatureFlags.RECO_HOME_PICKED_FOR_YOU,
  posthogFeatureFlags.STICKY_ATC_OFFSCREEN
]);

export const initAllFeatureFlags = async (): Promise<void> => {
  clearFeatureFlagsOnce();

  Object.values(posthogFeatureFlags)
    .filter((flag) => !LAZY_FEATURE_FLAGS.has(flag))
    .forEach((flag) => initFeatureFlag(flag));
};
